<script setup lang="ts">
import { Alert, RadioCardManySelect } from '@proj-airi/stage-ui/components'
import { useConsciousnessStore } from '@proj-airi/stage-ui/stores/modules/consciousness'
import { useProvidersStore } from '@proj-airi/stage-ui/stores/providers'
import { useSettings } from '@proj-airi/stage-ui/stores/settings'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  providerId: string
}>()

const { t } = useI18n()
const providersStore = useProvidersStore()
const consciousnessStore = useConsciousnessStore()
const settingsStore = useSettings()
const { activeProvider, activeModel, customModelName } = storeToRefs(consciousnessStore)

const modelSearchQuery = ref('')

const thinkingLevels = [
  { value: 'off' as const, label: '默认', tip: '不发送思考力度参数，由模型或服务商决定默认行为' },
  { value: 'low' as const, label: '轻思考', tip: '减少思考，更快响应' },
  { value: 'medium' as const, label: '中思考', tip: '适度思考' },
  { value: 'high' as const, label: '深思考', tip: '深度推理，响应较慢' },
]

const providerMetadata = computed(() => providersStore.getProviderMetadata(props.providerId))

const supportsModelListing = computed(() =>
  providerMetadata.value?.capabilities.listModels !== undefined,
)

const providerModels = computed(() =>
  providersStore.getModelsForProvider(props.providerId),
)

const isLoading = computed(() =>
  providersStore.isLoadingModels[props.providerId] || false,
)

const isActiveProvider = computed(() =>
  activeProvider.value === props.providerId,
)

// Sort models: active model first
const sortedModels = computed(() =>
  [...providerModels.value].sort((a, b) =>
    a.id === activeModel.value ? -1 : b.id === activeModel.value ? 1 : 0,
  ),
)

// When selecting a model, also activate this provider
const selectedModel = computed({
  get: () => isActiveProvider.value ? activeModel.value : '',
  set: (value: string) => {
    activeProvider.value = props.providerId
    activeModel.value = value
  },
})

function updateCustomModelName(value: string) {
  activeProvider.value = props.providerId
  customModelName.value = value
}

// Fetch models on mount and when providerId changes
watch(() => props.providerId, async (id) => {
  if (id && supportsModelListing.value && !isLoading.value && providerModels.value.length === 0) {
    await providersStore.fetchModelsForProvider(id)
  }
}, { immediate: true })
</script>

<template>
  <div v-if="providerMetadata?.category === 'chat'" flex="~ col gap-4">
    <div>
      <div flex items-center justify-between>
        <h3 class="text-lg font-medium">
          {{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.title') }}
        </h3>
        <!-- 思考力度选择器 -->
        <div flex items-center gap-1.5>
          <div i-solar:cpu-bold-duotone text-sm text="neutral-400 dark:neutral-500" />
          <div flex rounded-lg border="1 solid neutral-200 dark:neutral-700" bg="neutral-100 dark:neutral-800" p-0.5>
            <button
              v-for="level in thinkingLevels"
              :key="level.value"
              text="xs"
              rounded-md px-2 py-0.5 transition-all
              :class="settingsStore.thinkingLevel === level.value
                ? 'bg-white dark:bg-neutral-600 text-neutral-800 dark:text-neutral-100 shadow-sm font-medium'
                : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'"
              :title="level.tip"
              @click="settingsStore.thinkingLevel = level.value"
            >
              {{ level.label }}
            </button>
          </div>
        </div>
      </div>
      <div text="sm neutral-400 dark:neutral-500">
        <span v-if="isActiveProvider" class="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
          <span i-solar:check-circle-bold-duotone text-sm />
          当前正在使用此服务商
        </span>
        <span v-else>
          选择模型后将切换为使用此服务商
        </span>
      </div>
    </div>

    <template v-if="supportsModelListing">
      <!-- Loading state -->
      <div v-if="isLoading" class="flex items-center justify-center py-4">
        <div class="mr-2 animate-spin">
          <div i-solar:refresh-circle-line-duotone text-xl />
        </div>
        <span>{{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.loading') }}</span>
      </div>

      <!-- No models（错误态由外层 ProviderStatusPanel 接管，这里只显示"空列表"） -->
      <Alert
        v-else-if="providerModels.length === 0"
        type="warning"
      >
        <template #title>
          {{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.no_models') }}
        </template>
        <template #content>
          {{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.no_models_description') }}
        </template>
      </Alert>

      <!-- Model list -->
      <RadioCardManySelect
        v-else
        v-model="selectedModel"
        v-model:search-query="modelSearchQuery"
        :items="sortedModels"
        :searchable="true"
        :search-placeholder="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.search_placeholder')"
        :search-no-results-title="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.no_search_results')"
        :search-no-results-description="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.no_search_results_description', { query: modelSearchQuery })"
        :search-results-text="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.search_results', { count: '{count}', total: '{total}' })"
        :custom-input-placeholder="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.custom_model_placeholder')"
        :expand-button-text="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.expand')"
        :collapse-button-text="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.collapse')"
        @update:custom-value="updateCustomModelName"
      />
    </template>

    <!-- Provider doesn't support model listing -->
    <template v-else>
      <div
        class="flex items-center gap-3 border border-primary-200 rounded-lg bg-primary-50 p-4 dark:border-primary-800 dark:bg-primary-900/20"
      >
        <div i-solar:info-circle-line-duotone class="text-2xl text-primary-500 dark:text-primary-400" />
        <div class="flex flex-col">
          <span>{{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.not_supported') }}</span>
          <span class="text-sm text-primary-600 dark:text-primary-400">
            {{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.not_supported_description') }}
          </span>
        </div>
      </div>

      <!-- Manual model input -->
      <div>
        <label class="mb-1 block text-sm">
          {{ t('settings.pages.modules.consciousness.sections.section.provider-model-selection.manual_model_name') }}
        </label>
        <input
          :value="isActiveProvider ? activeModel : ''"
          type="text"
          class="w-full border border-neutral-300 rounded bg-white px-3 py-2 dark:border-neutral-700 dark:bg-neutral-900"
          :placeholder="t('settings.pages.modules.consciousness.sections.section.provider-model-selection.manual_model_placeholder')"
          @input="selectedModel = ($event.target as HTMLInputElement).value"
        >
      </div>
    </template>
  </div>
</template>
